export interface CyclicScrollOptions {
  element: HTMLElement;
  initialDelay?: number; // Initial delay before starting scroll (ms)
  bottomPause?: number; // Pause at bottom before scrolling back up (ms)
  topPause?: number; // Pause at top before starting next cycle (ms)
  durationMultiplier?: number; // Multiplier for scroll duration calculation (ms per pixel)
  minDuration?: number; // Minimum scroll duration (ms)
  maxDuration?: number; // Maximum scroll duration (ms)
  onStop?: () => void;
}

/**
 * Creates a cyclic scrolling animation that scrolls down, pauses, scrolls back up, and repeats
 * @param options Scrolling options
 * @returns Object with start and stop methods
 */
export function createCyclicScroll(options: CyclicScrollOptions) {
  const {
    element,
    initialDelay = 3000,
    bottomPause = 3000,
    topPause = 1000,
    durationMultiplier = 20,
    minDuration = 3000,
    maxDuration = 15000,
    onStop,
  } = options;

  let isScrolling = false;
  let scrollTimer: ReturnType<typeof setTimeout> | null = null;

  const easeInOut = (t: number): number => (t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t);
  const easeOut = (t: number): number => 1 - Math.pow(1 - t, 2);

  const startScrollAnimation = () => {
    stopScrollAnimation();
    isScrolling = true;

    scrollTimer = setTimeout(() => {
      if (!element || !isScrolling) return;

      if (element.scrollHeight <= element.clientHeight) {
        isScrolling = false;
        return;
      }

      const distance = element.scrollHeight - element.clientHeight;
      const duration = Math.min(Math.max(distance * durationMultiplier, minDuration), maxDuration);
      const rollbackDuration = Math.min(duration / 2, 1500);

      element.scrollTop = 0;

      const scrollDown = () => {
        const startTime = performance.now();

        const animate = (timestamp: number) => {
          if (!isScrolling) return;

          const elapsed = timestamp - startTime;
          const progress = Math.min(elapsed / duration, 1);
          const newPosition = distance * easeInOut(progress);

          element.scrollTop = newPosition;

          if (progress < 1) {
            requestAnimationFrame(animate);
          } else {
            // Pause at bottom then scroll back up
            setTimeout(() => {
              if (!isScrolling) return;
              scrollUp();
            }, bottomPause);
          }
        };

        requestAnimationFrame(animate);
      };

      const scrollUp = () => {
        const startTime = performance.now();

        const animate = (timestamp: number) => {
          if (!isScrolling) return;

          const elapsed = timestamp - startTime;
          const progress = Math.min(elapsed / rollbackDuration, 1);
          const newPosition = distance * (1 - easeOut(progress));

          element.scrollTop = newPosition;

          if (progress < 1) {
            requestAnimationFrame(animate);
          } else {
            // Pause at top then start next cycle
            setTimeout(() => {
              if (!isScrolling) return;
              scrollDown();
            }, topPause);
          }
        };

        requestAnimationFrame(animate);
      };

      scrollDown();
    }, initialDelay);
  };

  const stopScrollAnimation = () => {
    isScrolling = false;

    if (scrollTimer) {
      clearTimeout(scrollTimer);
      scrollTimer = null;
    }

    if (onStop) {
      onStop();
    }
  };

  return {
    startScrollAnimation: startScrollAnimation.bind(null),
    stopScrollAnimation: stopScrollAnimation.bind(null),
    isActive: () => isScrolling,
  };
}
